"use client";

import React from "react";
import { Phone, Mail, MapPin, User, CircleCheck, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const PROJECT_TYPES = ["Land Clearing", "Forestry Mulching", "Brush Removal", "Light Excavation", "Other"];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[400px] p-8 text-center space-y-6 bg-card/30 border border-border/50 rounded-lg backdrop-blur-sm animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-serif font-bold text-foreground">
            Inquiry Received
          </h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/50 text-primary hover:bg-primary/10 hover:text-primary">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-background/50 border-input/50 focus:border-primary/50 focus:ring-primary/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" {...field} className="bg-background/50 border-input/50 focus:border-primary/50 focus:ring-primary/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" {...field} className="bg-background/50 border-input/50 focus:border-primary/50 focus:ring-primary/20" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="property_address" rules={{
      required: "Property address is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">
              Property Address
            </FormLabel>
            <FormControl>
              <Input placeholder="123 Ranch Road, East Texas" {...field} className="bg-background/50 border-input/50 focus:border-primary/50 focus:ring-primary/20" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="project_type" rules={{
        required: "Please select a project type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Project Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-background/50 border-input/50 focus:border-primary/50 focus:ring-primary/20">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {PROJECT_TYPES.map(type => <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="estimated_acreage" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">
                Estimated Acreage
              </FormLabel>
              <FormControl>
                <Input type="number" placeholder="e.g., 15" {...field} className="bg-background/50 border-input/50 focus:border-primary/50 focus:ring-primary/20" />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/80">
                Approximate size of area to be cleared.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">
              Additional Details
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Provide any extra information about the project..." className="min-h-[120px] bg-background/50 border-input/50 focus:border-primary/50 focus:ring-primary/20 resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-md">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold tracking-wide uppercase bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20 transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing Request...
          </> : "Submit Inquiry"}
      </Button>
    </Form>;
}
export default function ProjectInquiry() {
  return <section id="project-inquiry" className="dark bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--primary)_0%,_transparent_30%)] opacity-5 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-20" />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
          {}
          <div className="space-y-10 lg:sticky lg:top-32">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-serif font-black tracking-tight text-foreground uppercase">
                Get a <span className="text-primary">Quote</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
                Ready to reclaim your land? Request a professional site
                evaluation and estimate for your property in East Texas.
              </p>
            </div>

            <div className="space-y-8">
              <div className="group">
                <div className="flex items-center gap-4 mb-2">
                  <div className="p-3 rounded-md bg-secondary/50 text-primary border border-primary/20 group-hover:border-primary/50 transition-colors">
                    <User className="size-6" />
                  </div>
                  <span className="text-sm font-bold text-muted-foreground uppercase tracking-wider">
                    Owner / Operator
                  </span>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-foreground pl-[4.5rem]">
                  Helanie Tresidder
                </p>
              </div>

              <div className="group">
                <div className="flex items-center gap-4 mb-2">
                  <div className="p-3 rounded-md bg-secondary/50 text-primary border border-primary/20 group-hover:border-primary/50 transition-colors">
                    <Phone className="size-6" />
                  </div>
                  <span className="text-sm font-bold text-muted-foreground uppercase tracking-wider">
                    Call Us Directly
                  </span>
                </div>
                <Link to="tel:9034904941" className="block text-2xl md:text-3xl font-bold text-foreground hover:text-primary transition-colors pl-[4.5rem]">
                  (903) 490-4941
                </Link>
              </div>

              <div className="group">
                <div className="flex items-center gap-4 mb-2">
                  <div className="p-3 rounded-md bg-secondary/50 text-primary border border-primary/20 group-hover:border-primary/50 transition-colors">
                    <Mail className="size-6" />
                  </div>
                  <span className="text-sm font-bold text-muted-foreground uppercase tracking-wider">
                    Email Us
                  </span>
                </div>
                <Link to="mailto:sharkbitetx@gmail.com" className="block text-xl md:text-2xl font-bold text-foreground hover:text-primary transition-colors pl-[4.5rem] break-all">
                  sharkbitetx@gmail.com
                </Link>
              </div>

              <div className="group">
                <div className="flex items-center gap-4 mb-2">
                  <div className="p-3 rounded-md bg-secondary/50 text-primary border border-primary/20 group-hover:border-primary/50 transition-colors">
                    <MapPin className="size-6" />
                  </div>
                  <span className="text-sm font-bold text-muted-foreground uppercase tracking-wider">
                    Service Area
                  </span>
                </div>
                <p className="text-xl md:text-2xl font-bold text-foreground pl-[4.5rem]">
                  East Texas
                </p>
              </div>
            </div>
          </div>

          {}
          <div className="bg-card/10 backdrop-blur-sm border border-border/40 p-6 md:p-8 lg:p-10 rounded-xl shadow-2xl shadow-black/20">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-foreground mb-2">
                Project Inquiry
              </h3>
              <p className="text-muted-foreground">
                Fill out the form below and we'll get back to you shortly to
                discuss your land clearing needs.
              </p>
            </div>

            <FormProvider formId="project_inquiry_form_get_a_quote" sectionName="project-inquiry" defaultValues={{
            name: "",
            email: "",
            phone: "",
            property_address: "",
            project_type: "",
            estimated_acreage: "",
            details: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            property_address: {
              required: true
            },
            project_type: {
              required: true
            }
          }} submitText="Submit Inquiry" successMessage="Thank you! Your project inquiry has been received. We will contact you shortly." errorMessage="Failed to submit inquiry. Please check your connection and try again.">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}